import React, { useCallback, useState } from 'react';
import { Button, Intent } from '@blueprintjs/core';
import PinPad from '../PinPad';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { AuthState } from '../../views/auth/types';
import {
  doPinLogin,
  getUserProfile,
  setInstantLogin,
  setUsername,
} from '../../views/auth/reducer';
import { getTokenRemainingTimeInSeconds } from '../../utils/getAuthToken';
import useToaster from '../../hooks/useToaster';

interface AuthButtonProps {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}

const AuthButton = ({ children, onClick, disabled }: AuthButtonProps) => {
  const { showErrorMessage } = useToaster();
  const dispatch = useAppDispatch();
  const state: AuthState = useAppSelector((state) => state.auth);

  const [localLoading, setLocalLoading] = useState<boolean>(false);

  const executeCallBack = useCallback(() => {
    onClick();
  }, [onClick]);

  const doSetUserName = useCallback(
    (value: string) => {
      dispatch(setUsername(value));
    },
    [dispatch],
  );

  // Special Strange Code...
  const doLoginPreparation = useCallback(async () => {
    setLocalLoading(true);
    await dispatch(getUserProfile())
      .then((action) => {
        // Check jwt token validity
        const time = getTokenRemainingTimeInSeconds();

        // Token time is less than one minute
        if (getUserProfile.rejected.match(action) || time < 60) {
          // On reject, (ot timed out), we will try to login again
          return dispatch(setInstantLogin(true));
        } else {
          executeCallBack();
        }
      })
      .catch((error) => {
        showErrorMessage(error.message);
      });
    setLocalLoading(false);
  }, [dispatch, executeCallBack, showErrorMessage]);

  const doLoginWithPin = useCallback(
    (pin: string) => {
      dispatch(
        doPinLogin({
          username: state.login.username,
          pin,
        }),
      )
        .then((action) => {
          if (doPinLogin.fulfilled.match(action)) {
            return dispatch(getUserProfile());
          } else {
            throw new Error('Falha no Login...');
          }
        })
        .then((action) => {
          if (getUserProfile.fulfilled.match(action)) {
            return executeCallBack();
          } else {
            throw new Error('Falha no Login...');
          }
        })
        .catch((error) => {
          showErrorMessage(error.message);
        });
    },
    [dispatch, executeCallBack, showErrorMessage, state.login.username],
  );

  return (
    <>
      <Button
        id="auth-button"
        fill
        large
        onClick={doLoginPreparation}
        disabled={localLoading || disabled}
        loading={localLoading}
        rightIcon="chevron-right"
        intent={Intent.PRIMARY}
      >
        {children}
      </Button>
      {state.instantLogin.pinRequested && (
        <PinPad
          username={state.login.username}
          onSetUsername={doSetUserName}
          onPinAction={doLoginWithPin}
          isLoading={state.isLoading}
        />
      )}
    </>
  );
};

export default AuthButton;
